/**
 * Formatting Utilities
 *
 * Common formatting functions for displaying data consistently across the app
 */

/**
 * Truncate a UUID or long ID to a shorter, more readable format
 *
 * @param id - The full ID/UUID to truncate
 * @param length - Number of characters to keep (default: 8)
 * @returns Truncated ID with ellipsis
 *
 * @example
 * ```ts
 * truncateId('a84acc30-febd-4475-b90b-e55516d76d92') // 'a84acc30...'
 * truncateId('a84acc30-febd-4475-b90b-e55516d76d92', 6) // 'a84acc...'
 * ```
 */
export function truncateId(id: string | undefined | null, length: number = 8): string {
  if (!id) return 'N/A';
  if (id.length <= length) return id;
  return `${id.substring(0, length)}...`;
}

/**
 * Format a date string to a readable format
 *
 * @param dateString - ISO date string
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(
  dateString: string | undefined | null,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString(undefined, options);
  } catch {
    return 'Invalid Date';
  }
}

/**
 * Format a date string to a readable time format
 *
 * @param dateString - ISO date string
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted time string
 */
export function formatTime(
  dateString: string | undefined | null,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleTimeString(undefined, options);
  } catch {
    return 'Invalid Time';
  }
}

/**
 * Format a snake_case or lowercase label to title case with spaces
 *
 * @param label - The label to format (e.g., "book_no", "patient-id", "patinetId")
 * @returns Formatted label (e.g., "Book No", "Patient Id", "PatientId")
 */
export function formatLabel(label: string): string {
  const words = label.replace(/[_,-]/g, ' ').trim().split(/\s+/).filter(Boolean);

  return words
    .map((word) =>
      word.toUpperCase() === word
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');
}
